'use client';

import { ReportSuccessPopup } from '@/features/report-form/ui/report-success-popup';
import { API_CREATE_REPORT } from '@/shared/constants/bff-endpoints';
import { format } from 'date-fns';
import { useState, useTransition } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Text, Textarea, Button } from '@/shared/ui/core';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import { ClearableInput } from '@/shared/ui/kit/clearable-input';
import { ControlledSelect } from '@/shared/ui/kit/controlled-select';
import { ControlledDatePicker } from '@/shared/ui/kit/controlled-datepicker';
import { ControlledFileUpload } from '@/shared/ui/kit/controlled-file-upload';
import { reportFormSchema, ReportFormInput, ReportFormOutput } from '@/shared/lib/validation/report/report.schema';
import { MAX_FILES } from '@/shared/config/report-form';
import { CARGO_CATEGORIES } from '@/shared/constants/cargo-categories';
import { COUNTRIES } from '@/shared/constants/countries';
import styles from './report-form.module.scss';


interface ComponentProps {
    companyName?: string;
    vatNumber?: string;
    email?: string;
    your_email?: string;
}


export function ReportForm ({ companyName, email, vatNumber, your_email }: ComponentProps) {
    const [ isPending, startTransition ] = useTransition();
    const [ success, setSuccess ] = useState(false);

    const methods = useForm<ReportFormInput, unknown, ReportFormOutput>({
        resolver      : zodResolver(reportFormSchema),
        mode          : 'onBlur',
        reValidateMode: 'onChange',
        defaultValues : {
            email         : email ?? '',
            company_name  : companyName ?? '',
            vat_number    : vatNumber ?? '',
            cargo_category: '',
            route_from    : '',
            route_to      : '',
            your_email    : your_email ?? '',
            incident_date : undefined,
            description   : '',
            files         : [],
        },
    });

    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors },
        setError,
    } = methods;

    const emailVal       = useWatch({ control, name: 'email' });
    const companyNameVal = useWatch({ control, name: 'company_name' });
    const vatNumberVal   = useWatch({ control, name: 'vat_number' });
    const yourEmailVal   = useWatch({ control, name: 'your_email' });
    const descriptionVal = useWatch({ control, name: 'description' });

    const onSubmit = handleSubmit((data) => {
        startTransition(async () => {
            const fd = new FormData();

            fd.append('email',          data.email);
            fd.append('company_name',   data.company_name);
            fd.append('vat_number',     data.vat_number ?? '');
            fd.append('cargo_category', data.cargo_category ?? '');
            fd.append('route_from',     data.route_from ?? '');
            fd.append('route_to',       data.route_to ?? '');
            fd.append('your_email',     data.your_email);
            fd.append('incident_date',  data.incident_date ?? '');
            fd.append('description',    data.description);

            data.files?.forEach((file) => fd.append('files', file));

            const res = await fetch(API_CREATE_REPORT, {
                method: 'POST',
                body  : fd,
            });

            if (!res.ok) {
                const body = await res.json().catch(() => ({}));

                if (body && typeof body === 'object') {
                    Object.entries(body).forEach(([ field, messages ]) => {
                        if (Array.isArray(messages)) {
                            setError(field as keyof ReportFormInput, { message: messages[0] });
                        }
                    });
                }

                return;
            }

            methods.reset();
            setSuccess(true);
        });
    });

    return (
        <FormProvider { ...methods }>
            <div className={ styles.row }>
                <ClearableInput
                    label="Subcontractor contact email:"
                    placeholder="example@domain.com"
                    description="Enter the subcontractor contact email address"
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    required
                    error={ errors.email?.message }
                    value={ emailVal ?? '' }
                    onClear={ () => setValue('email', '', { shouldValidate: true }) }
                    { ...register('email') }
                />

                <ClearableInput
                    label="Company name:"
                    placeholder="Example LLC"
                    description="Enter the company name"
                    required
                    error={ errors.company_name?.message }
                    value={ companyNameVal ?? '' }
                    onClear={ () => setValue('company_name', '', { shouldValidate: true }) }
                    { ...register('company_name') }
                />

                <ClearableInput
                    label="VAT number:"
                    placeholder="Enter VAT number"
                    description="Enter VAT number"
                    error={ errors.vat_number?.message }
                    value={ vatNumberVal ?? '' }
                    onClear={ () => setValue('vat_number', '', { shouldValidate: true }) }
                    { ...register('vat_number') }
                />
            </div>

            <div className={ styles.row }>
                <ControlledSelect<ReportFormInput>
                    name="cargo_category"
                    label="Cargo category:"
                    options={ CARGO_CATEGORIES }
                    placeholder="Select cargo category"
                    searchable
                />

                <ControlledSelect<ReportFormInput>
                    name="route_from"
                    label="Route from:"
                    options={ COUNTRIES }
                    placeholder="Select route from"
                    searchable
                />

                <ControlledSelect<ReportFormInput>
                    name="route_to"
                    label="Route to:"
                    options={ COUNTRIES }
                    placeholder="Select route to"
                    searchable
                />
            </div>

            <div className={ styles.row }>
                <ClearableInput
                    label="Your email:"
                    placeholder="example@domain.com"
                    type="email"
                    autoComplete="email"
                    required
                    error={ errors.your_email?.message }
                    value={ yourEmailVal ?? '' }
                    onClear={ () => setValue('your_email', '', { shouldValidate: true }) }
                    { ...register('your_email') }
                />

                <ControlledDatePicker<ReportFormInput>
                    name="incident_date"
                    label="Date of incident:"
                    placeholder="Select date"
                    disabledDay={ { after: new Date() } }
                    format={ (date) => format(date, 'dd / MM / yyyy') }
                    required
                />
            </div>

            <Textarea
                className={ styles.textarea }
                label="Description:"
                placeholder="Enter the description of the incident"
                required
                error={ errors.description?.message }
                value={ descriptionVal ?? '' }
                { ...register('description') }
            />

            <RequiredDataNote/>

            <div className={ styles.files_section }>
                <Text
                    size="xl"
                    tone="primary"
                    weight="semibold"
                    lineHeight={ 1.2 }
                >
                    Add a file
                </Text>

                <Text
                    size="lg"
                    tone="secondary"
                    weight="regular"
                    lineHeight={ 1.2 }
                    className={ styles.files_description }
                >
                    If you have any additional information about the incident, you can attach it as a file (screenshot, text document, etc.)
                </Text>

                <ControlledFileUpload<ReportFormInput>
                    name="files"
                    className={ styles.files_field }
                    accept={ '.pdf, .txt, .jpeg, .jpg, .png, .doc, .docx, .xls, .xlsx' }
                    maxFiles={ MAX_FILES }
                    multiple
                />
            </div>

            <Button
                loading={ isPending }
                className={ styles.button }
                onClick={ onSubmit }
            >
                Report your incident
            </Button>

            {
                success &&
                <ReportSuccessPopup
                    open={ success }
                    onClose={ () => setSuccess(false) }
                />
            }
        </FormProvider>
    );
}
